import React, { useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from './Sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './Dialog';
import { Button } from './Button';
import { Input } from './Input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './Tabs';
import { useN8nApi } from '../helpers/useN8nApi';
import { N8nWorkflow } from '../helpers/n8nApi';
import { WorkflowListPanel } from './WorkflowListPanel';
import { WorkflowGenerateDialog } from './WorkflowGenerateDialog';
import { WorkflowBuilder } from './WorkflowBuilder';
import {
  useCreateWorkflow,
  useUpdateWorkflow,
  type Workflow as TaskingWorkflow,
} from '../helpers/useWorkflowsApi';
import { WorkflowDefinition } from '../helpers/workflowTypes';
import { toast } from 'sonner';
import {
  Play,
  Pause,
  Trash2,
  Plus,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  Loader2,
  ExternalLink,
  Settings,
  Workflow,
  Sparkles,
  Link,
} from 'lucide-react';
import styles from './N8nPanel.module.css';

interface N8nPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sessionId?: string;
}

export const N8nPanel: React.FC<N8nPanelProps> = ({ open, onOpenChange, sessionId }) => {
  const {
    isConnected,
    isConnecting,
    isLoading,
    error,
    workflows,
    executions,
    selectedWorkflow,
    instanceUrl: connectedInstanceUrl,
    connect,
    disconnect,
    refreshWorkflows,
    selectWorkflow,
    createWorkflow,
    deleteWorkflow,
    toggleWorkflowActive,
    executeWorkflow,
    clearError,
  } = useN8nApi();

  const [instanceUrlInput, setInstanceUrlInput] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [isEditingCredentials, setIsEditingCredentials] = useState(false);
  const [newWorkflowName, setNewWorkflowName] = useState('');
  const [showNewWorkflowInput, setShowNewWorkflowInput] = useState(false);
  const [view, setView] = useState<'list' | 'detail'>('list');
  const [activeTab, setActiveTab] = useState<'ai' | 'external'>('ai');
  const [isGenerateOpen, setIsGenerateOpen] = useState(false);
  const [isBuilderOpen, setIsBuilderOpen] = useState(false);
  const [editingWorkflow, setEditingWorkflow] = useState<TaskingWorkflow | null>(null);
  const [builderWorkflow, setBuilderWorkflow] = useState<WorkflowDefinition | undefined>(undefined);

  const createWorkflowMutation = useCreateWorkflow();
  const updateWorkflowMutation = useUpdateWorkflow();

  const handleConnect = async () => {
    if (!instanceUrlInput || !apiKey) return;
    await connect({ instanceUrl: instanceUrlInput, apiKey });
    setIsEditingCredentials(false);
  };

  const handleEditCredentials = () => {
    setInstanceUrlInput(connectedInstanceUrl || '');
    setApiKey('');
    setIsEditingCredentials(true);
  };

  const handleCreateWorkflow = async () => {
    if (!newWorkflowName.trim()) return;
    try {
      await createWorkflow(newWorkflowName.trim());
      setNewWorkflowName('');
      setShowNewWorkflowInput(false);
    } catch (e) {
      // Error handled by hook
    }
  };

  const handleSelectWorkflow = async (workflow: N8nWorkflow) => {
    await selectWorkflow(workflow.id);
    setView('detail');
  };

  const handleBackToList = () => {
    selectWorkflow(null);
    setView('list');
  };

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleString('en-US');
    } catch {
      return dateStr;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle className={styles.statusSuccess} size={16} />;
      case 'error':
        return <XCircle className={styles.statusError} size={16} />;
      case 'running':
        return <Loader2 className={`${styles.statusRunning} ${styles.spin}`} size={16} />;
      default:
        return <Clock className={styles.statusWaiting} size={16} />;
    }
  };

  // Handler for creating a new workflow manually
  const handleCreateManual = () => {
    setBuilderWorkflow(undefined);
    setEditingWorkflow(null);
    setIsBuilderOpen(true);
  };

  // Handler for AI-generated workflow editing
  const handleEditAiWorkflow = (workflow: TaskingWorkflow) => {
    // Parse the stored workflow data
    try {
      const nodes = workflow.nodes ? JSON.parse(workflow.nodes) : [];
      const connections = workflow.connections ? JSON.parse(workflow.connections) : [];

      setBuilderWorkflow({
        id: workflow.id,
        name: workflow.name,
        description: workflow.description || undefined,
        nodes,
        connections,
        createdAt: workflow.createdAt,
        updatedAt: workflow.updatedAt,
      });
      setEditingWorkflow(workflow);
      setIsBuilderOpen(true);
    } catch (e) {
      toast.error('Failed to load workflow data');
    }
  };

  // Handler for saving workflow from builder
  const handleBuilderSave = async (workflowDef: WorkflowDefinition) => {
    try {
      if (editingWorkflow) {
        // Update existing workflow
        await updateWorkflowMutation.mutateAsync({
          id: editingWorkflow.id,
          name: workflowDef.name,
          nodes: JSON.stringify(workflowDef.nodes),
          connections: JSON.stringify(workflowDef.connections),
        });
        toast.success('Workflow updated');
      } else {
        // Create new workflow
        await createWorkflowMutation.mutateAsync({
          name: workflowDef.name,
          sessionId,
          nodes: JSON.stringify(workflowDef.nodes),
          connections: JSON.stringify(workflowDef.connections),
        });
        toast.success('Workflow created');
      }
      setIsBuilderOpen(false);
      setEditingWorkflow(null);
      setBuilderWorkflow(undefined);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to save workflow');
    }
  };

  const handleGenerateSuccess = (workflowId: string, n8nUrl?: string) => {
    setIsGenerateOpen(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className={styles.panel}>
        <SheetHeader>
          <SheetTitle className={styles.panelTitle}>
            <Workflow size={20} />
            Workflows
          </SheetTitle>
          <SheetDescription>
            Create AI-powered workflows or connect to your n8n instance
          </SheetDescription>
        </SheetHeader>

        {error && (
          <div className={styles.error}>
            <span>{error}</span>
            <button onClick={clearError} className={styles.errorClose}>
              &times;
            </button>
          </div>
        )}

        <Tabs
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as 'ai' | 'external')}
          className={styles.tabs}
        >
          <TabsList className={styles.tabsList}>
            <TabsTrigger value="ai" className={styles.tabTrigger}>
              <Sparkles size={14} />
              AI Workflows
            </TabsTrigger>
            <TabsTrigger value="external" className={styles.tabTrigger}>
              <Link size={14} />
              External n8n
            </TabsTrigger>
          </TabsList>

          <TabsContent value="ai" className={styles.tabContent}>
            <WorkflowListPanel
              sessionId={sessionId}
              onGenerateWithAI={() => setIsGenerateOpen(true)}
              onCreateNew={handleCreateManual}
              onEditWorkflow={handleEditAiWorkflow}
            />
            <WorkflowGenerateDialog
              open={isGenerateOpen}
              onOpenChange={setIsGenerateOpen}
              sessionId={sessionId}
              onSuccess={handleGenerateSuccess}
            />
          </TabsContent>

          <TabsContent value="external" className={styles.tabContent}>
            {!isConnected || isEditingCredentials ? (
              <div className={styles.connectForm}>
                {isEditingCredentials && (
                  <div className={styles.editCredentialsBanner}>
                    Update your n8n connection credentials below.
                  </div>
                )}
                <div className={styles.formGroup}>
                  <label className={styles.label}>n8n Instance URL</label>
                  <Input
                    placeholder="https://n8n.yourdomain.com"
                    value={instanceUrlInput}
                    onChange={(e) => setInstanceUrlInput(e.target.value)}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.label}>API Key</label>
                  <Input
                    id="n8n-api-key"
                    name="n8n-api-key"
                    type="password"
                    placeholder={isEditingCredentials ? 'Enter new API key' : 'Your n8n API key'}
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                  />
                  <p className={styles.hint}>
                    Generate an API key in n8n: Settings &rarr; API &rarr; Create API Key
                  </p>
                </div>
                {!isEditingCredentials && (
                  <div className={styles.apiNote}>
                    <strong>Note:</strong> n8n's API is not available for trial versions. You must
                    have a paid n8n subscription to use this integration.
                  </div>
                )}
                <div className={styles.connectFormActions}>
                  <Button
                    onClick={handleConnect}
                    disabled={isConnecting || !instanceUrlInput || !apiKey}
                    className={styles.connectButton}
                  >
                    {isConnecting ? (
                      <>
                        <Loader2 className={styles.spin} size={16} />
                        Connecting...
                      </>
                    ) : isEditingCredentials ? (
                      'Update Connection'
                    ) : (
                      'Connect to n8n'
                    )}
                  </Button>
                  {isEditingCredentials && (
                    <Button variant="ghost" onClick={() => setIsEditingCredentials(false)}>
                      Cancel
                    </Button>
                  )}
                </div>
              </div>
            ) : view === 'list' ? (
              <div className={styles.workflowsContainer}>
                <div className={styles.toolbar}>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={refreshWorkflows}
                    disabled={isLoading}
                  >
                    <RefreshCw size={14} className={isLoading ? styles.spin : ''} />
                    Refresh
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setShowNewWorkflowInput(true)}
                  >
                    <Plus size={14} />
                    New
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleEditCredentials}
                    title="Edit connection credentials"
                  >
                    <Settings size={14} />
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={disconnect}
                    className={styles.disconnectBtn}
                  >
                    Disconnect
                  </Button>
                </div>

                {showNewWorkflowInput && (
                  <div className={styles.newWorkflowForm}>
                    <Input
                      placeholder="Workflow name"
                      value={newWorkflowName}
                      onChange={(e) => setNewWorkflowName(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleCreateWorkflow()}
                      autoFocus
                    />
                    <Button
                      size="sm"
                      onClick={handleCreateWorkflow}
                      disabled={!newWorkflowName.trim()}
                    >
                      Create
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setShowNewWorkflowInput(false);
                        setNewWorkflowName('');
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                )}

                <div className={styles.workflowList}>
                  {workflows.length === 0 ? (
                    <div className={styles.emptyState}>
                      <Workflow size={48} className={styles.emptyIcon} />
                      <p>No workflows found</p>
                      <p className={styles.emptyHint}>Create a new workflow to get started</p>
                    </div>
                  ) : (
                    workflows.map((workflow) => (
                      <div
                        key={workflow.id}
                        className={styles.workflowItem}
                        onClick={() => handleSelectWorkflow(workflow)}
                      >
                        <div className={styles.workflowInfo}>
                          <span className={styles.workflowName}>{workflow.name}</span>
                          <span className={styles.workflowMeta}>
                            {workflow.nodes?.length || 0} nodes
                          </span>
                        </div>
                        <div className={styles.workflowActions}>
                          <span
                            className={`${styles.statusBadge} ${
                              workflow.active ? styles.active : styles.inactive
                            }`}
                          >
                            {workflow.active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                <div className={styles.instanceInfo}>
                  <span className={styles.connectedTo}>Connected to:</span>
                  <a
                    href={connectedInstanceUrl || ''}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.instanceLink}
                  >
                    {connectedInstanceUrl}
                    <ExternalLink size={12} />
                  </a>
                </div>
              </div>
            ) : selectedWorkflow ? (
              <div className={styles.detailContainer}>
                <div className={styles.detailHeader}>
                  <button onClick={handleBackToList} className={styles.backButton}>
                    &larr; Back
                  </button>
                  <h3 className={styles.workflowTitle}>{selectedWorkflow.name}</h3>
                </div>

                <div className={styles.detailActions}>
                  <Button
                    variant={selectedWorkflow.active ? 'secondary' : 'primary'}
                    size="sm"
                    onClick={() =>
                      toggleWorkflowActive(selectedWorkflow.id, !selectedWorkflow.active)
                    }
                    disabled={isLoading}
                  >
                    {selectedWorkflow.active ? (
                      <>
                        <Pause size={14} /> Deactivate
                      </>
                    ) : (
                      <>
                        <Play size={14} /> Activate
                      </>
                    )}
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => executeWorkflow(selectedWorkflow.id)}
                    disabled={isLoading}
                  >
                    <Play size={14} /> Run Now
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      if (confirm('Delete this workflow?')) {
                        deleteWorkflow(selectedWorkflow.id);
                        handleBackToList();
                      }
                    }}
                    className={styles.deleteBtn}
                  >
                    <Trash2 size={14} />
                  </Button>
                  <a
                    href={`${connectedInstanceUrl}/workflow/${selectedWorkflow.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.openInN8n}
                  >
                    Open in n8n <ExternalLink size={12} />
                  </a>
                </div>

                <div className={styles.detailSection}>
                  <h4>Nodes ({selectedWorkflow.nodes?.length || 0})</h4>
                  <div className={styles.nodeList}>
                    {selectedWorkflow.nodes?.map((node) => (
                      <div key={node.id} className={styles.nodeItem}>
                        <span className={styles.nodeName}>{node.name}</span>
                        <span className={styles.nodeType}>
                          {node.type.replace('n8n-nodes-base.', '')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className={styles.detailSection}>
                  <h4>Recent Executions</h4>
                  {executions.length === 0 ? (
                    <p className={styles.noExecutions}>No executions yet</p>
                  ) : (
                    <div className={styles.executionList}>
                      {executions.slice(0, 10).map((exec) => (
                        <div key={exec.id} className={styles.executionItem}>
                          {getStatusIcon(exec.status)}
                          <span className={styles.execTime}>{formatDate(exec.startedAt)}</span>
                          <span className={`${styles.execStatus} ${styles[exec.status]}`}>
                            {exec.status}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className={styles.detailSection}>
                  <h4>Details</h4>
                  <div className={styles.metaGrid}>
                    <div>
                      <span className={styles.metaLabel}>Created</span>
                      <span>{formatDate(selectedWorkflow.createdAt)}</span>
                    </div>
                    <div>
                      <span className={styles.metaLabel}>Updated</span>
                      <span>{formatDate(selectedWorkflow.updatedAt)}</span>
                    </div>
                    <div>
                      <span className={styles.metaLabel}>ID</span>
                      <span className={styles.monoText}>{selectedWorkflow.id}</span>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </TabsContent>
        </Tabs>

        {/* Workflow Builder Dialog */}
        <Dialog
          open={isBuilderOpen}
          onOpenChange={(open) => {
            if (!open) {
              setIsBuilderOpen(false);
              setEditingWorkflow(null);
              setBuilderWorkflow(undefined);
            }
          }}
        >
          <DialogContent className={styles.builderDialog}>
            <DialogHeader>
              <DialogTitle>{editingWorkflow ? 'Edit Workflow' : 'Create Workflow'}</DialogTitle>
            </DialogHeader>
            <WorkflowBuilder workflow={builderWorkflow} onSave={handleBuilderSave} />
          </DialogContent>
        </Dialog>
      </SheetContent>
    </Sheet>
  );
};
